/**
 * Undo and redo (F15) over plan snapshots, capped at the policy's undo_limit.
 *
 * Use `run(plan, action)` for every committed change: it snapshots first and
 * keeps the snapshot only when the action returns `{ ok: true }`. A drag is one
 * step: `run` around its `begin...`, then move freely, then end.
 */
import { POLICY } from './policy';
import type { Plan } from './types';

export const snapshot = (plan: Plan): string =>
  JSON.stringify({ rooms: plan.rooms, defaults: plan.defaults, next: plan.next });
export const restore = (js: string): Plan => JSON.parse(js) as Plan;

export class History {
  undoStack: string[] = [];
  redoStack: string[] = [];
  constructor(public limit: number = POLICY.undo_limit) {}

  get canUndo(): boolean { return this.undoStack.length > 0; }
  get canRedo(): boolean { return this.redoStack.length > 0; }

  /** Record the state before a change. Clears redo. */
  checkpoint(plan: Plan): void {
    this.push(snapshot(plan));
    this.redoStack = [];
  }

  private push(s: string): void {
    this.undoStack.push(s);
    while (this.undoStack.length > this.limit) this.undoStack.shift();
  }

  /** Drop the last checkpoint (an action that turned out to change nothing). */
  discard(): void { this.undoStack.pop(); }

  /** Run an action; keep a checkpoint only if it changed the plan. */
  run<T extends { ok: boolean }>(plan: Plan, action: (p: Plan) => T): T {
    const before = snapshot(plan);
    const res = action(plan);
    if (res && res.ok) { this.push(before); this.redoStack = []; }
    return res;
  }

  /** The previous plan, or null. The caller replaces its plan with it. */
  undo(current: Plan): Plan | null {
    const s = this.undoStack.pop();
    if (s == null) return null;
    this.redoStack.push(snapshot(current));
    return restore(s);
  }

  redo(current: Plan): Plan | null {
    const s = this.redoStack.pop();
    if (s == null) return null;
    this.push(snapshot(current));
    return restore(s);
  }
}
